import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Printer, 
  Settings, 
  Copy, 
  Trash2, 
  CheckCircle2, 
  Download,
  CreditCard,
  UserSquare2,
  FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { stationeryAPI } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const Stationery = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await stationeryAPI.listTemplates();
      setTemplates(res.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await stationeryAPI.deleteTemplate(id);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await stationeryAPI.updateTemplate(template.id, { is_default: true });
      toast.success('Default template updated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update default template');
    }
  };

  const StationeryCard = ({ template }) => (
    <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300 border-slate-200 bg-white">
      <div className="aspect-[1.5/1] bg-slate-50 flex items-center justify-center relative border-b border-slate-100">
         <div className="text-slate-300 transition-transform group-hover:scale-110 duration-500">
            {template.type === 'ID_CARD' ? <UserSquare2 size={64} /> : <CreditCard size={64} />}
         </div>
         {template.is_default && (
           <div className="absolute top-3 right-3 bg-primary text-white px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 shadow-md">
             <CheckCircle2 size={10} /> DEFAULT
           </div>
         )}
      </div>
      <CardHeader className="p-4 space-y-1">
        <CardTitle className="text-base font-bold truncate text-slate-900 group-hover:text-primary transition-colors">{template.name}</CardTitle>
        <CardDescription className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{template.type === 'ID_CARD' ? 'ID Card Template' : 'Business Card Template'}</CardDescription>
      </CardHeader>
      <CardFooter className="p-4 pt-0 flex justify-between items-center gap-2">
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-primary hover:bg-primary/5" onClick={() => navigate(`/stationery/designer/${template.type.toLowerCase()}/${template.id}`)}>
            <Settings size={14} />
          </Button>
          {!template.is_default && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-green-600 hover:bg-green-50" onClick={() => handleSetDefault(template)}>
              <CheckCircle2 size={14} />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(template.id)}>
            <Trash2 size={14} />
          </Button>
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs font-bold border-slate-200 hover:bg-slate-50 transition-colors" onClick={() => navigate(`/stationery/generate/${template.id}`)}>
          <Printer size={14} /> Generate
        </Button>
      </CardFooter>
    </Card>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Printer className="text-primary" size={32} /> Stationery Assets
          </h1>
          <p className="text-slate-500 font-medium mt-1">Design and manage professional ID cards and business cards for your workforce.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => navigate('/stationery/designer/id_card')} className="font-bold gap-2 shadow-sm">
            <Plus size={18} /> New ID Template
          </Button>
          <Button onClick={() => navigate('/stationery/designer/business_card')} variant="outline" className="font-bold gap-2 border-slate-200">
            <Plus size={18} /> New Business Card
          </Button>
        </div>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="bg-slate-100 p-1 border border-slate-200 mb-8 w-full md:w-auto h-auto grid grid-cols-3 md:flex">
          <TabsTrigger value="all" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm px-8 py-2 font-bold text-xs uppercase tracking-wider transition-all">All Assets</TabsTrigger>
          <TabsTrigger value="id_card" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm px-8 py-2 font-bold text-xs uppercase tracking-wider transition-all">ID Cards</TabsTrigger>
          <TabsTrigger value="business_card" className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm px-8 py-2 font-bold text-xs uppercase tracking-wider transition-all">Business Cards</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-0 focus-visible:ring-0">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-64 rounded-xl bg-slate-200 animate-pulse" />)}
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-2xl bg-white/50">
              <div className="h-20 w-20 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-4 border border-primary/10">
                <Printer size={40} className="text-primary/60" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No templates configured</h3>
              <p className="text-slate-500 mb-6 max-w-sm mx-auto font-medium">Create your first design template to start generating professional stationery for your team.</p>
              <Button onClick={() => navigate('/stationery/designer/id_card')} className="px-8 font-bold">
                Create First Template
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {templates.map(t => <StationeryCard key={t.id} template={t} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="id_card" className="mt-0 focus-visible:ring-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {templates.filter(t => t.type === 'ID_CARD').map(t => <StationeryCard key={t.id} template={t} />)}
          </div>
        </TabsContent>

        <TabsContent value="business_card" className="mt-0 focus-visible:ring-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {templates.filter(t => t.type === 'BUSINESS_CARD').map(t => <StationeryCard key={t.id} template={t} />)}
          </div>
        </TabsContent>
      </Tabs>

      <div className="pt-8 border-t border-slate-100">
          <h2 className="text-sm font-black uppercase tracking-[0.2em] text-slate-400 mb-6 font-mono">Operations & Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-slate-200 bg-white hover:border-primary/50 hover:shadow-md transition-all group cursor-pointer" onClick={() => navigate('/stationery/generate/bulk')}>
              <CardHeader className="flex flex-row items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-300">
                    <Download size={24} strokeWidth={2.5} />
                </div>
                <div>
                    <CardTitle className="text-lg font-bold">Bulk Generation</CardTitle>
                    <CardDescription className="font-medium">Batch process cards for departments or entire offices.</CardDescription>
                </div>
              </CardHeader>
            </Card>
            
            <Card className="border-slate-200 bg-white hover:border-primary/50 hover:shadow-md transition-all group cursor-pointer" onClick={() => navigate('/employees')}>
              <CardHeader className="flex flex-row items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-green-50 flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition-all duration-300 text-green-600">
                    <UserSquare2 size={24} strokeWidth={2.5} />
                </div>
                <div>
                    <CardTitle className="text-lg font-bold">Employee Directory</CardTitle>
                    <CardDescription className="font-medium">Locate and generate assets for individual employees.</CardDescription>
                </div>
              </CardHeader>
            </Card>
          </div>
      </div>
    </div>
  );
};

export default Stationery;
